import PropTypes from 'prop-types'
import { useState, useEffect, useRef } from 'react'
import Button from 'react-bootstrap/Button'
import { useNavigate } from 'react-router-dom'

import { fechaEnRango } from '../../helpers/dateValidation.js'
import { tieneMedioPagoHabilitado } from '../../helpers/mediosPago.js'
import { toastErrorLog } from '../../helpers/toastErrorLog.js'
import { useAuth } from '../../hooks/useAuth'
import { MediosPagoChips, medioPagoPropType } from '../../ui/widgets/index.js'
import { obtenerPrecioUnitarioMayorista, toNumber } from '../helpers/calcularPrecioTotal.js'
import Imagen from '../imagen/Imagen.jsx'
import { InputConTextoSugerencia } from '../reutilizables/inputConTextoSugerencia/InputConTextoSugerencia.jsx'
import Precios from '../reutilizables/precios/Precios'
import { SumarProductos } from '../reutilizables/sumarProductos/SumarProductos.jsx'
import * as carritoService from './../servicios/carritoService'
import styles from './CarritoItem.module.css'

const CarritoItem = (props) => {
  const { centroDeCostos, adjunto, loadingImagenes, item } = props
  const { mediosPagos = [], cantDisponible } = item
  const { loginDataContext: { idUsuario } = {} } = useAuth()
  const [cantidad, setCantidad] = useState(props.item.cantidad)
  const [sugerencias, setSugerencias] = useState([])
  const [sugerenciasImputacion, setSugerenciasImputacion] = useState([])
  const [sugerenciasCodMat, setSugerenciasCodMat] = useState([])

  const deshabilitaCompra = !tieneMedioPagoHabilitado(item)

  const sumar = () => {
    if (cantidad < cantDisponible || cantidad < props.item.capacidadMax) {
      setCantidad(cantidad + 1)
      props.sumar(props.totalSuma + props.item.precioUnitario)
    }
  }

  const restar = () => {
    if (cantidad > 1) {
      setCantidad(cantidad - 1)
      props.sumar(props.totalSuma - props.item.precioUnitario)
    }
  }

  const navigate = useNavigate()
  const isMounted = useRef(false)

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      return
    }

    const carrito = {
      idPublicacion: props.item.idPublicacion,
      idUsuario,
      cantidad: cantidad,
      idEstado: 1
    }

    const actualizarCarrito = async (carrito) => {
      try {
        await carritoService.actualizar(carrito)
      } catch (error) {
        const mensaje = `Ocurrió un error al actualizar el carrito.`
        toastErrorLog(error, mensaje, 'carrito')
      }
    }
    actualizarCarrito(carrito)
    props.setItems((prevItems) =>
      prevItems.map((item) =>
        item.idPublicacion === carrito.idPublicacion
          ? { ...item, cantidad: carrito.cantidad }
          : item
      )
    )
  }, [cantidad, idUsuario])

  const handleSelectSugerencias = (descripcion, propiedad) => {
    props.modificarCarrito(
      descripcion.descripcion,
      propiedad,
      props.item.idPublicacion,
      descripcion.codigo
    )
    setSugerencias([])
    setSugerenciasImputacion([])
    setSugerenciasCodMat([])
  }

  const handleChange = (texto, propiedad, setSugerencias, array, idPublicacion) => {
    const opcionSeleccionada = array.find(
      (opcion) => opcion.codigo === texto || opcion.descripcion === texto
    )

    const codigo = opcionSeleccionada?.codigo

    props.modificarCarrito(texto, propiedad, idPublicacion || props.item.idPublicacion, codigo)

    setSugerencias(
      texto.trim() === ''
        ? []
        : array.filter(
            (opcion) => opcion.codigo?.includes(texto) || opcion.descripcion?.includes(texto)
          )
    )
  }

  const copiarImputacion = (categoria, value) => {
    props.copiar(categoria, value)
    props.copiar('imputacionTipo', props.item.imputacionTipo)
  }

  const obtenerPrecioUnitario = () => {
    const precioMayorista = obtenerPrecioUnitarioMayorista(props.item, cantidad)
    if (precioMayorista > 0) return precioMayorista

    const totalConIva = toNumber(props.item.valorTotalConIva)
    const cant = toNumber(cantidad) || 1
    return cant > 0 ? totalConIva / cant : 0
  }

  const esMayorista = () => {
    if (!props.item.preciosMayoristas || props.item.preciosMayoristas.length === 0) {
      return false
    }

    return props.item.preciosMayoristas.some((precioMayorista) => {
      const cantTier = toNumber(precioMayorista.cantidad)
      const precioTier = toNumber(precioMayorista.precioUnitario)
      return cantTier > 0 && precioTier > 0 && cantidad >= cantTier
    })
  }

  const porcDcto = Number(props.item.porcDcto) || 0
  const ofertaVigente = fechaEnRango(
    new Date(),
    props.item?.ofertaDesde,
    props.item?.ofertaHasta,
    true
  )
  const tieneDescuento = ofertaVigente && porcDcto > 0 && porcDcto < 100

  const precioUnitarioFinal = obtenerPrecioUnitario()
  const precioOriginal = tieneDescuento ? precioUnitarioFinal : null

  return (
    <>
      <div
        className={`${styles.carritoItem}${deshabilitaCompra ? ` ${styles.deshabilitado}` : ''}`}
      >
        <div className={styles.carritoRow}>
          <div className={styles.celdaImagen}>
            <Imagen
              adjunto={adjunto}
              loadingImagenes={loadingImagenes}
              width={'100px'}
              height={'100px'}
            />
          </div>

          <div className={styles.celdaTitulo}>
            <Button
              variant="link"
              className={styles.tituloLink}
              onClick={() => navigate(`/Publicacion/${props.item.idPublicacion}`)}
            >
              {props.item.titulo}
            </Button>
          </div>
          <div className={styles.celdaPrecios}>
            <Precios
              precio={precioUnitarioFinal}
              monedaSimbolo={props.item.monedaSimbolo}
              alicuota={props.item.alicuota}
              cantidad={cantidad}
              showSinIVA={true}
              porcDcto={porcDcto}
              precioOriginal={precioOriginal}
              tieneDescuento={tieneDescuento}
              sufijo={esMayorista() ? '(mayorista)' : ''}
              className={styles.carritoItemPreciosItem}
              classNameSinIva={styles.carritoItemPreciosSinIva}
            />
          </div>
          <div className={styles.celdaCantidad}>
            <SumarProductos
              cantidad={cantidad}
              setCantidad={setCantidad}
              decrementar={restar}
              incrementar={sumar}
              cantDisponible={cantDisponible || props.item.capacidadMax}
              agregarAlCarrito={null}
              aPedido={props.item.aPedido}
              cantMin={props.item.cantMinPedido}
            />
          </div>

          <div className={styles.celdaEliminar}>
            <Button
              variant="light"
              onClick={() => props.onDelete(props.item.idPublicacion)}
              className={styles.btnBackground}
              title="Eliminar del carrito"
            >
              <i className={`bi bi-trash-fill ${styles.btnIconColor}`}></i>
            </Button>
          </div>
        </div>
        <div className={`${styles.bloque2}`}>
          <InputConTextoSugerencia
            texto={'Código de material'}
            codigo={`codMat-${props.item.codigo}`}
            value={props.item.codMat || ''}
            copiar={props.copiar}
            setSugerencias={setSugerenciasCodMat}
            arrayChance={centroDeCostos?.catalogoMateriales}
            sugerencias={sugerenciasCodMat}
            categoria={'codMat'}
            handleSelectSugerencias={handleSelectSugerencias}
            handleChange={handleChange}
          />
          <InputConTextoSugerencia
            texto={'Planta'}
            codigo={`inputPlanta-${props.item.codigo}`}
            value={props.item.valorPlanta || ''}
            copiar={props.copiar}
            setSugerencias={setSugerencias}
            arrayChance={centroDeCostos?.usuariosPlantaEfectiva}
            sugerencias={sugerencias}
            categoria={'valorPlanta'}
            handleSelectSugerencias={handleSelectSugerencias}
            handleChange={handleChange}
          />

          <InputConTextoSugerencia
            texto={'Imputación'}
            codigo={`imputacion-${props.item.codigo}`}
            value={props.item.imputacionTipo || ''}
            copiar={props.copiar}
            arrayChance={centroDeCostos?.opcionesImputTpos}
            categoria={'imputacionTipo'}
            modificarCarrito={props.modificarCarrito}
            idPublicacion={props.item.idPublicacion}
          />

          {props.item.imputacionTipo && (
            <InputConTextoSugerencia
              texto={
                centroDeCostos?.opcionesImputTpos?.find(
                  (opcion) => opcion.value === props.item.imputacionTipo
                )?.label
              }
              codigo={`inputPlanta-${props.item.codigo}`}
              value={props.item.imputacionTipoOpcion || ''}
              copiar={copiarImputacion}
              setSugerencias={setSugerenciasImputacion}
              arrayChance={centroDeCostos?.listaImputaciones?.filter(
                (opcion) => opcion.tipoImpu === props.item.imputacionTipo
              )}
              sugerencias={sugerenciasImputacion}
              categoria={'imputacionTipoOpcion'}
              handleSelectSugerencias={handleSelectSugerencias}
              handleChange={handleChange}
            />
          )}
        </div>
        <MediosPagoChips mediosPagos={mediosPagos} />
      </div>
      <hr />
    </>
  )
}

CarritoItem.propTypes = {
  centroDeCostos: PropTypes.object,
  item: PropTypes.shape({
    cantidad: PropTypes.number,
    cantDisponible: PropTypes.number,
    precioUnitario: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    idPublicacion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    valorTotalConIva: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    valorTotalSinIva: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cantidadMayorista: PropTypes.number,
    monedaSimbolo: PropTypes.string,
    titulo: PropTypes.string,
    codMat: PropTypes.string,
    codigo: PropTypes.string,
    valorPlanta: PropTypes.string,
    imputacionTipo: PropTypes.string,
    imputacionTipoOpcion: PropTypes.string,
    alicuota: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    preciosMayoristas: PropTypes.array,
    porcDcto: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    empid: PropTypes.string,
    mediosPagos: PropTypes.arrayOf(medioPagoPropType),
    capacidadMax: PropTypes.number,
    aPedido: PropTypes.bool,
    cantMinPedido: PropTypes.number,
    ofertaDesde: PropTypes.string,
    ofertaHasta: PropTypes.string
  }).isRequired,
  sumar: PropTypes.func.isRequired,
  totalSuma: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setItems: PropTypes.func.isRequired,
  modificarCarrito: PropTypes.func.isRequired,
  copiar: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  adjunto: PropTypes.shape({
    url: PropTypes.string.isRequired,
    nombre: PropTypes.string,
    idPublicacion: PropTypes.number
  }),
  loadingImagenes: PropTypes.bool.isRequired
}

export default CarritoItem
